import { useEffect, useId, useMemo, useState } from "react";
import { DataTable, Input, Modal, Page } from "@iq/models";
import { FiBriefcase, FiEye, FiPlus, FiSave, FiUserPlus } from "react-icons/fi";
import { createAccount, getAccount, getAccounts, getLeadForgeAssessment, saveLeadForgeAssessment, updateAccount } from "../services/account_service";
import NewContact from "../components/modals/NewContact";
import useLookupStore from "../states/useLookupStore";
import "../styles/accountsPage.css";

const ACCOUNT_FIELDS = [
    ["name", "Account name", true],
    ["website", "Website"],
    ["domain", "Domain"],
    ["industry", "Industry"],
    ["subindustry", "Subindustry"],
    ["city", "City"],
    ["state", "State"],
    ["source", "Lead source"],
];

const EMPTY_ASSESSMENT = {
    contact_id: "",
    lead_tier: "",
    primary_problem: "",
    problem_evidence: "",
    recommended_offer: "",
    call_angle: "",
    contact_role: "",
    contact_confidence: "",
    assessment_confidence: "",
};

const ACCOUNTS_TABLE_CONFIG = {
    columns: [
        { key: "name", label: "Account", accessor: (row) => row?.name || "-", filterable: true, width: "2fr" },
        { key: "industry", label: "Industry", accessor: (row) => row?.industry || "-", filterable: true },
        { key: "location", label: "Location", accessor: (row) => [row?.city, row?.state].filter(Boolean).join(", ") || "-" },
        { key: "source", label: "Lead Source", accessor: (row) => row?.source || "-", filterable: true },
        { key: "contacts", label: "Contacts", accessor: (row) => String(row?.contact_count ?? 0) },
        { key: "deals", label: "Deals", accessor: (row) => String(row?.deal_count ?? 0) },
    ],
};

function AccountField({ name, label, value, onChange, required = false, wide = false, ...inputProps }) {
    return (
        <div className={`crm-account-field${wide ? " crm-account-field--wide" : ""}`}>
            <Input
                id={name}
                name={name}
                label={<>{label}{required ? <span className="crm-account-required"> *</span> : null}</>}
                value={value ?? ""}
                onChange={onChange ? (event) => onChange(event.target.value) : undefined}
                colorScheme="light"
                required={required}
                {...inputProps}
            />
        </div>
    );
}

function contactLabel(contact) {
    return [contact?.first_name, contact?.last_name].filter(Boolean).join(" ") || contact?.email || "Unnamed contact";
}

function NewAccountModal({ onClose, onCreated }) {
    const formId = useId();
    const [form, setForm] = useState(() => Object.fromEntries(ACCOUNT_FIELDS.map(([key]) => [key, ""])));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function handleSubmit(event) {
        event.preventDefault();
        if (!form.name.trim()) {
            setError("Account name is required");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const account = await createAccount(form);
            onCreated(account);
        } catch (saveError) {
            setError(saveError.message || "Failed to create account");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            isOpen={true}
            onClose={saving ? undefined : onClose}
            title="New Account"
            icon={<FiBriefcase />}
            actionButtons={[{
                key: "create-account",
                text: saving ? "Creating account..." : "Create account",
                icon: <FiSave aria-hidden="true" />,
                type: "submit",
                form: formId,
                disabled: saving,
                color: "accent",
            }]}
            errorMessage={error}
            titleClassName="crm-account-modal-title"
            wide={true}
        >
            <form id={formId} className="crm-account-form" onSubmit={handleSubmit}>
                <div className="crm-account-fields">
                    {ACCOUNT_FIELDS.map(([key, label, required]) => (
                        <AccountField
                            key={key}
                            name={key}
                            label={label}
                            type={key === "website" ? "url" : "text"}
                            value={form[key]}
                            required={Boolean(required)}
                            disabled={saving}
                            onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                        />
                    ))}
                </div>
            </form>
        </Modal>
    );
}

export function AccountDetailsModal({ accountId, onAddContact, onClose, onSaved, onContactSelect }) {
    const formId = useId();
    const [account, setAccount] = useState(null);
    const [form, setForm] = useState({});
    const [assessment, setAssessment] = useState(EMPTY_ASSESSMENT);
    const [loading, setLoading] = useState(true);
    const [accountSaving, setAccountSaving] = useState(false);
    const [assessmentSaving, setAssessmentSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        Promise.all([getAccount(accountId), getLeadForgeAssessment(accountId)])
            .then(([result, assessmentResult]) => {
                if (!cancelled) {
                    setAccount(result);
                    setForm(Object.fromEntries(ACCOUNT_FIELDS.map(([key]) => [key, result?.[key] || ""])));
                    setAssessment({
                        ...EMPTY_ASSESSMENT,
                        ...(assessmentResult || {}),
                        contact_id: assessmentResult?.contact_id ? String(assessmentResult.contact_id) : "",
                        contact_confidence: assessmentResult?.contact_confidence ?? "",
                        assessment_confidence: assessmentResult?.assessment_confidence ?? "",
                    });
                }
            })
            .catch((loadError) => !cancelled && setError(loadError.message || "Failed to load account"))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [accountId]);

    async function handleAccountSubmit(event) {
        event.preventDefault();
        if (!String(form.name || "").trim()) {
            setError("Account name is required");
            return;
        }
        setAccountSaving(true);
        setError("");
        try {
            const updated = await updateAccount(accountId, form);
            setAccount((current) => ({ ...current, ...updated }));
            onSaved?.(updated);
        } catch (saveError) {
            setError(saveError.message || "Failed to update account");
        } finally {
            setAccountSaving(false);
        }
    }

    async function handleAssessmentSave() {
        setAssessmentSaving(true);
        setError("");
        try {
            const saved = await saveLeadForgeAssessment(accountId, {
                ...assessment,
                contact_id: assessment.contact_id ? Number(assessment.contact_id) : null,
                contact_confidence: assessment.contact_confidence === "" ? null : Number(assessment.contact_confidence),
                assessment_confidence: assessment.assessment_confidence === "" ? null : Number(assessment.assessment_confidence),
            });
            setAssessment({ ...EMPTY_ASSESSMENT, ...(saved || {}) });
        } catch (saveError) {
            setError(saveError.message || "Failed to save LeadForge assessment");
        } finally {
            setAssessmentSaving(false);
        }
    }

    const actionButtons = [
        {
            key: "save-assessment",
            text: assessmentSaving ? "Saving assessment..." : "Save assessment",
            icon: <FiSave aria-hidden="true" />,
            type: "button",
            onClick: handleAssessmentSave,
            disabled: loading || accountSaving || assessmentSaving,
        },
        {
            key: "save-account",
            text: accountSaving ? "Saving account..." : "Save account",
            icon: <FiSave aria-hidden="true" />,
            type: "submit",
            form: formId,
            disabled: loading || accountSaving || assessmentSaving,
            color: "accent",
        },
    ];

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={account?.name || "Account"}
            icon={<FiBriefcase />}
            actionButtons={actionButtons}
            errorMessage={error}
            titleClassName="crm-account-modal-title"
            wide={true}
            tall={true}
        >
            {loading ? <div className="crm-account-empty">Loading account...</div> : (
                <form id={formId} className="crm-account-form" onSubmit={handleAccountSubmit}>
                    <div className="crm-account-fields">
                        {ACCOUNT_FIELDS.map(([key, label, required]) => (
                            <AccountField
                                key={key}
                                name={key}
                                label={label}
                                type={key === "website" ? "url" : "text"}
                                value={form[key]}
                                required={Boolean(required)}
                                onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                            />
                        ))}
                    </div>
                    <section className="crm-account-contacts" aria-labelledby="account-contacts-heading">
                        <div className="crm-account-section-heading">
                            <h3 id="account-contacts-heading">Contacts</h3>
                            {onAddContact ? (
                                <button type="button" className="crm-account-add-contact" onClick={() => onAddContact(account)}>
                                    <FiUserPlus aria-hidden="true" />
                                    <span>Add contact</span>
                                </button>
                            ) : null}
                        </div>
                        {account?.contacts?.length ? account.contacts.map((contact) => {
                            const content = (
                                <>
                                    <strong>{[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unnamed contact"}</strong>
                                    <span>{contact.title || contact.email || "No role or email"}</span>
                                </>
                            );
                            return onContactSelect ? (
                                <button key={contact.id} type="button" className="crm-account-contact-row" onClick={() => onContactSelect(contact)}>
                                    {content}
                                </button>
                            ) : (
                                <div key={contact.id} className="crm-account-contact-row crm-account-contact-row--static">
                                    {content}
                                </div>
                            );
                        }) : <div className="crm-account-empty">No contacts linked to this account.</div>}
                    </section>
                    <section className="crm-leadforge-assessment" aria-labelledby="leadforge-assessment-heading">
                        <div className="crm-leadforge-assessment-heading">
                            <h3 id="leadforge-assessment-heading">LeadForge Assessment</h3>
                            <span>Sales intelligence</span>
                        </div>
                        <div className="crm-account-fields">
                            <AccountField name="lead-tier" label="Lead tier" value={assessment.lead_tier} onChange={(value) => setAssessment((current) => ({ ...current, lead_tier: value }))} />
                            <AccountField name="recommended-offer" label="Recommended offer" value={assessment.recommended_offer} onChange={(value) => setAssessment((current) => ({ ...current, recommended_offer: value }))} />
                            <AccountField name="primary-problem" label="Primary problem" type="textarea" rows={3} wide={true} value={assessment.primary_problem} onChange={(value) => setAssessment((current) => ({ ...current, primary_problem: value }))} />
                            <AccountField name="problem-evidence" label="Problem evidence" type="textarea" rows={4} wide={true} value={assessment.problem_evidence} onChange={(value) => setAssessment((current) => ({ ...current, problem_evidence: value }))} />
                            <AccountField name="call-angle" label="Call angle" type="textarea" rows={3} wide={true} value={assessment.call_angle} onChange={(value) => setAssessment((current) => ({ ...current, call_angle: value }))} />
                            <AccountField
                                name="assessed-contact"
                                label="Assessed contact"
                                type="dropdown"
                                value={assessment.contact_id ? contactLabel((account?.contacts || []).find((contact) => String(contact.id) === String(assessment.contact_id))) : "Account-level"}
                                options={[
                                    { value: "", label: "Account-level" },
                                    ...(account?.contacts || []).map((contact) => ({ value: String(contact.id), label: contactLabel(contact) })),
                                ]}
                                allowCustom={false}
                                onOptionSelect={(option) => setAssessment((current) => ({ ...current, contact_id: option.value }))}
                            />
                            <AccountField name="contact-role" label="Contact role" value={assessment.contact_role} onChange={(value) => setAssessment((current) => ({ ...current, contact_role: value }))} />
                            <AccountField name="contact-confidence" label="Contact confidence (%)" type="number" min="0" max="100" value={assessment.contact_confidence} onChange={(value) => setAssessment((current) => ({ ...current, contact_confidence: value }))} />
                            <AccountField name="assessment-confidence" label="Assessment confidence (%)" type="number" min="0" max="100" value={assessment.assessment_confidence} onChange={(value) => setAssessment((current) => ({ ...current, assessment_confidence: value }))} />
                        </div>
                    </section>
                </form>
            )}
        </Modal>
    );
}

function Accounts({ onContactSelect }) {
    const [accounts, setAccounts] = useState([]);
    const [showNewAccount, setShowNewAccount] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [contactAccount, setContactAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const hydrateAccounts = useLookupStore((state) => state.hydrateAccounts);

    async function loadAccounts() {
        setLoading(true);
        try {
            setAccounts(await getAccounts());
        } catch (error) {
            console.error("Failed to load accounts", error);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadAccounts(); }, []);

    const rowActions = useMemo(() => (row) => [{
        id: "view-account",
        label: "View",
        icon: <FiEye aria-hidden="true" />,
        onClick: () => setSelectedAccountId(row.id),
    }], []);

    return (
        <Page>
            <DataTable
                rowData={accounts}
                loading={loading}
                tableConfig={ACCOUNTS_TABLE_CONFIG}
                headerActions={[{
                    id: "new-account",
                    label: "New Account",
                    color: "accent",
                    icon: <FiPlus aria-hidden="true" />,
                    onClick: () => setShowNewAccount(true),
                }]}
                onRowClick={(account) => setSelectedAccountId(account.id)}
                rowActions={rowActions}
            />
            {showNewAccount ? (
                <NewAccountModal
                    onClose={() => setShowNewAccount(false)}
                    onCreated={(account) => {
                        setShowNewAccount(false);
                        setSelectedAccountId(account.id);
                        loadAccounts();
                        hydrateAccounts({ force: true });
                    }}
                />
            ) : null}
            {selectedAccountId ? (
                <AccountDetailsModal
                    accountId={selectedAccountId}
                    onAddContact={(account) => {
                        setContactAccount(account);
                        setSelectedAccountId(null);
                    }}
                    onClose={() => setSelectedAccountId(null)}
                    onSaved={loadAccounts}
                    onContactSelect={(contact) => {
                        setSelectedAccountId(null);
                        onContactSelect?.({ ...contact, name: [contact.first_name, contact.last_name].filter(Boolean).join(" ") });
                    }}
                />
            ) : null}
            {contactAccount ? (
                <NewContact
                    initialContact={{
                        account: contactAccount.name,
                        accountId: contactAccount.id,
                        company: contactAccount.name,
                    }}
                    onClose={() => setContactAccount(null)}
                    onCreated={() => {
                        const accountId = contactAccount.id;
                        setContactAccount(null);
                        setSelectedAccountId(accountId);
                        loadAccounts();
                    }}
                />
            ) : null}
        </Page>
    );
}

export default Accounts;